import React, { useState, useEffect, useCallback } from 'react';
import { StudyCalendarEntry, CalendarPeriod } from '../types';
import { fetchCalendarEntries, createCalendarEntry, toggleCalendarEntry, deleteCalendarEntry } from '../lib/api';
import { Calendar as CalendarIcon, Clock, CheckSquare, Square, Trash2, Plus, AlertCircle } from 'lucide-react';

export const CalendarPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [entries, setEntries] = useState<StudyCalendarEntry[]>([]);
  const [, setLoading] = useState<boolean>(true);

  // Form state
  const [period, setPeriod] = useState<CalendarPeriod>('morning');
  const [focus, setFocus] = useState<string>('science');
  const [todo, setTodo] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const wordCount = todo.trim().split(/\s+/).filter(Boolean).length;
  const isOverWordLimit = wordCount > 30;

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCalendarEntries(selectedDate);
      setEntries(data);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleCreateEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isOverWordLimit) {
      setErrorMsg('O to-do ultrapassa o limite estrito de 30 palavras.');
      return;
    }
    if (!todo.trim()) {
      setErrorMsg('Preencha a tarefa antes de salvar.');
      return;
    }

    try {
      setErrorMsg(null);
      await createCalendarEntry({
        date: selectedDate,
        period,
        focus,
        todo,
      });
      setTodo('');
      await loadEntries();
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao adicionar entrada.');
    }
  };

  const handleToggle = async (id: string, currentStatus: boolean) => {
    await toggleCalendarEntry(id, !currentStatus);
    await loadEntries();
  };

  const handleDelete = async (id: string) => {
    await deleteCalendarEntry(id);
    await loadEntries();
  };

  const periodLabels: Record<CalendarPeriod, { label: string; time: string }> = {
    morning: { label: 'Manhã', time: '08:00 — 12:00' },
    afternoon: { label: 'Tarde', time: '14:00 — 18:00' },
    evening: { label: 'Noite', time: '19:00 — 22:30' },
  };

  const focusPresets = [
    'science',
    'Caderno de Erros',
    'Exame Antigo 2024.1',
    'Exame Antigo 2024.2',
    'Exame Antigo 2025.1',
    'Exame Antigo 2025.2',
    'Exame Antigo 2026.1',
  ];

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-paper-50 px-4 py-8 dark:bg-study-slate sm:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h1 className="font-serif text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                Calendário Estratégico de Estudos
              </h1>
            </div>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              Planejamento por turnos (Manhã / Tarde / Noite) com foco direcionado e to-do objetivo (máx. 30 palavras).
            </p>
          </div>

          <div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="rounded border border-paper-300 bg-paper-100 px-3 py-1.5 font-mono text-xs font-semibold text-zinc-800 dark:border-study-line dark:bg-study-card dark:text-zinc-200"
            />
          </div>
        </div>

        {/* Creation form */}
        <form
          onSubmit={handleCreateEntry}
          className="mb-8 rounded-lg border border-paper-300 bg-paper-100 p-5 shadow-sm dark:border-study-line dark:bg-study-panel"
        >
          <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100 border-b border-paper-200 pb-2 dark:border-study-line">
            Adicionar Meta para {selectedDate}
          </h3>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">
                Período / Turno:
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as CalendarPeriod)}
                className="w-full rounded border border-paper-300 bg-paper-50 p-2 text-xs font-semibold text-zinc-900 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
              >
                <option value="morning">Manhã (morning)</option>
                <option value="afternoon">Tarde (afternoon)</option>
                <option value="evening">Noite (evening)</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">
                Foco do Estudo:
              </label>
              <select
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                className="w-full rounded border border-paper-300 bg-paper-50 p-2 text-xs font-semibold text-zinc-900 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
              >
                {focusPresets.map((preset) => (
                  <option key={preset} value={preset}>
                    {preset}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                To-Do Objetivo:
              </label>
              <span
                className={`font-mono text-[10px] font-bold ${
                  isOverWordLimit ? 'text-red-600' : 'text-zinc-500 dark:text-zinc-400'
                }`}
              >
                {wordCount} / 30 palavras {isOverWordLimit && '(LIMITE ULTRAPASSADO)'}
              </span>
            </div>
            <textarea
              rows={2}
              value={todo}
              onChange={(e) => setTodo(e.target.value)}
              placeholder="ex: Fazer triagem de 10 questões de Cinemática e revisar fórmulas de Torricelli no caderno de erros."
              className={`w-full rounded border p-2.5 text-xs text-zinc-900 focus:outline-none dark:text-zinc-100 ${
                isOverWordLimit
                  ? 'border-red-500 bg-red-50/20 dark:border-red-500'
                  : 'border-paper-300 bg-paper-50 focus:border-indigo-500 dark:border-study-line dark:bg-study-card'
              }`}
            />
          </div>

          {errorMsg && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-semibold">
              <AlertCircle className="h-3.5 w-3.5" />
              {errorMsg}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={isOverWordLimit || !todo.trim()}
              className="flex items-center gap-1.5 rounded bg-zinc-900 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-zinc-800 disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
            >
              <Plus className="h-3.5 w-3.5" />
              Salvar Entrada
            </button>
          </div>
        </form>

        {/* Entries list by period */}
        <div className="space-y-6">
          {(['morning', 'afternoon', 'evening'] as CalendarPeriod[]).map((p) => {
            const periodEntries = entries.filter((e) => e.period === p);
            const periodInfo = periodLabels[p];

            return (
              <div
                key={p}
                className="rounded-lg border border-paper-300 bg-paper-100 p-5 dark:border-study-line dark:bg-study-panel"
              >
                <div className="flex items-center justify-between border-b border-paper-200 pb-2 dark:border-study-line mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-zinc-500" />
                    <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
                      {periodInfo.label}
                    </h3>
                    <span className="font-mono text-[10px] text-zinc-400">
                      ({periodInfo.time})
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-zinc-500 dark:text-zinc-400">
                    {periodEntries.length} item(ns)
                  </span>
                </div>

                {periodEntries.length === 0 ? (
                  <p className="text-xs text-zinc-400 italic py-2">
                    Nenhum bloco agendado para este período.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {periodEntries.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-start justify-between gap-3 rounded border p-3 transition ${
                          item.completed
                            ? 'border-emerald-300 bg-emerald-50/30 text-zinc-500 dark:border-emerald-950 dark:bg-emerald-950/10'
                            : 'border-paper-200 bg-paper-50 text-zinc-800 dark:border-study-line dark:bg-study-card dark:text-zinc-200'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleToggle(item.id, item.completed)}
                          className="mt-0.5 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                        >
                          {item.completed ? (
                            <CheckSquare className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>

                        <div className="flex-1 text-xs">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-paper-200 text-zinc-700 dark:bg-study-line dark:text-zinc-300">
                              {item.focus}
                            </span>
                          </div>
                          <p className={`font-sans leading-relaxed ${item.completed ? 'line-through' : ''}`}>
                            {item.todo}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="text-zinc-400 hover:text-red-600 transition"
                          title="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
